import { getProcesses, navigateToProcessOrTask, startProcess } from '@/api/backend/workflow';
import { WorkDefinitionResponseType } from '@/api/backend/types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  SearchInput,
  Skeleton,
} from '@opaz/ui-components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const AddProcessDialog = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<WorkDefinitionResponseType | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleClose = () => {
    setIsOpen(false);
    setSelectedProcess(null);
    setSearchQuery('');
  };

  const { data: processes, isPending } = useQuery({
    queryKey: ['processes'],
    queryFn: async () => {
      const response = await getProcesses();
      const filteredProcesses = response.filter((process) => !process.category?.startsWith('http://'));
      return filteredProcesses;
    },
  });

  const filteredProcesses = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return processes ?? [];
    }
    return (processes ?? []).filter((process) => process.name.toLowerCase().includes(query));
  }, [processes, searchQuery]);

  const { mutate: handleStartProcess, isPending: isCreatingProcess } = useMutation({
    mutationFn: async (data: { processId: string; category: string }) => {
      const createdProcess = await startProcess({
        processDefinitionId: data.processId,
        serviceCode: data.category,
      });
      await navigateToProcessOrTask(createdProcess.id, navigate);
      handleClose();
    },
  });

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setSelectedProcess(null);
          setSearchQuery('');
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="secondary" size="icon">
          <Plus className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-screen-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('addProcess.createNew')}</DialogTitle>
          <DialogDescription>{t('addProcess.selectItem')}</DialogDescription>
        </DialogHeader>
        {selectedProcess && (
          <Button
            variant="secondary"
            onClick={() => {
              setSelectedProcess(null);
            }}
            className="w-[max-content]"
          >
            <ArrowLeft className="size-4" />
            {t('common.back')}
          </Button>
        )}
        {!selectedProcess ? (
          <div className="grid gap-4 py-4">
            {isPending ? (
              <div className="flex items-center justify-center">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              <>
                <SearchInput
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClear={() => setSearchQuery('')}
                  placeholder={t('multiselect.searchPlaceholder')}
                  aria-label={t('common.search')}
                />
                {filteredProcesses.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('common.emptyMessage')}</p>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {filteredProcesses.map((process) => (
                      <Button
                        key={process.id}
                        variant="secondary"
                        className="w-full"
                        onClick={() => setSelectedProcess(process)}
                      >
                        <div className="flex flex-col items-start w-full">
                          <p className="text-md font-medium">{process.name}</p>
                        </div>
                      </Button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div>
            <p className="text-md font-medium">{selectedProcess.name}</p>
            <Button
              className="mt-2"
              disabled={isCreatingProcess}
              onClick={() =>
                handleStartProcess({
                  processId: selectedProcess.id,
                  category: selectedProcess.category,
                })
              }
            >
              {isCreatingProcess ? t('addProcess.creating') : t('addProcess.create')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddProcessDialog;
